import {
  Button,
  Col,
  Flex,
  Row,
  Select,
  Spin,
} from 'antd'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import Page from '@/components/shared/page'
import { FeatureKey } from '@/constants/feature-key'
import {
  ChatterPerformance,
  ChatterOverviewSales as ChatterOverviewSalesModel,
} from '@/model/analytics'
import { readChatterOverviewSales, readChatterPerformance } from '@/redux/chatter-reports-actions'
import { readAllEmployees } from '@/redux/employee-actions'
import { selectEmployeeOptions } from '@/redux/employee-selector'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import s from './chatter-reports.module.css'
import ChatterOverviewSales
  from './components/chatter-overview/chatter-overview-sales'
import ChatterPerformanceActivity
  from './components/chatter-performance/chatter-performance-activity'
import ChatterPerformanceFanChatted
  from './components/chatter-performance/chatter-performance-fan-chatted'
import ChatterPerformanceGoldenRatio
  from './components/chatter-performance/chatter-performance-golden-ratio'
import ChatterPerformanceMessageCount
  from './components/chatter-performance/chatter-performance-message-count'
import ChatterPerformanceUnlockRatio
  from './components/chatter-performance/chatter-performance-unlock-ratio'
import { ReportsPageProps } from './hooks/use-report-search'

const ChatterPerformanceLabel = {
  messageSent: 'Messages sent',
  ppvsSent: 'PPVs sent',
  ppvsUnlocked: 'PPVs unlocked',
  fansChatted: 'Fans chatted',
  chatterActivity: 'Chatter activity',
  goldenRatio: 'Golden ratio',
  unlockRatio: 'Unlock ratio',
}

interface EmployeeSelectFormProps {
  value: number
  employeeOptions: { label: string, value: number }[]
  onChange?: (employeeId: number) => void
}

export function EmployeeSelectForm({
  value,
  onChange,
  employeeOptions,
}: EmployeeSelectFormProps) {

  const onSelectChange = useCallback((employeeId: number) => {
    onChange?.(employeeId)
  }, [onChange])

  return (
    <div className={s.EmployeeSelectForm}>
      <Flex justify="space-between" gap={24}>
        <Flex gap={8} className={s.Employees}>
          {employeeOptions.map(e => {
            return (
              <Button
                key={e.value}
                type={e.value === value ? 'primary' : 'default'}
                onClick={() => onSelectChange(e.value)}>
                {e.label}
              </Button>
            )
          })}
        </Flex>
        <Select
          value={value}
          options={employeeOptions}
          style={{ width: 200 }}
          onChange={onSelectChange}
        />
      </Flex>
    </div>
  )
}

const selector = createAppSelector(
  [
    selectEmployeeOptions,
    state => state.chatterReports.chatterPerformance,
  ],
  (employeeOptions, chatterPerformance) => ({ employeeOptions, chatterPerformance }),
)

export default function CreatorReportsPerformance({
  searchValues,
}: ReportsPageProps) {
  const dispatch = useAppDispatch()
  const { employeeOptions, chatterPerformance } = useAppSelector(selector)

  const [loadingEmployees, setLoadingEmployees] = useState(false)
  const [loadingChatterPerformance, setLoadingChatterPerformance] = useState(false)
  const [loadingChatterSales, setLoadingChatterSales] = useState(false)
  const [employeeId, setEmployeeId] = useState<number | undefined>(employeeOptions[0]?.value)
  const [chatterSalesReport, setChatterSalesReport] = useState<ChatterOverviewSalesModel | undefined>()

  useEffect(() => {
    const loadEmployees = async () => {
      setLoadingEmployees(true)
      const { data } = await dispatch(readAllEmployees({ permissionKey: FeatureKey.ChattersPerformance })).unwrap()
      if (data.rows.length > 0) {
        setEmployeeId(data.rows[0].id)
      }
      setLoadingEmployees(false)
    }
    void loadEmployees()
  }, [dispatch])

  useEffect(() => {
    const loadChatterPerformance = async () => {
      if (employeeId === undefined) return
      setLoadingChatterPerformance(true)
      await dispatch(readChatterPerformance({
        ...searchValues,
        employeeId,
        permissionKey: FeatureKey.ChattersPerformance,
      }))
      setLoadingChatterPerformance(false)
    }
    void loadChatterPerformance()
  }, [dispatch, searchValues, employeeId])

  useEffect(() => {
    const loadChatterSalesReport = async () => {
      if (employeeId === undefined) return
      setLoadingChatterSales(true)
      const result = await dispatch(readChatterOverviewSales({
        ...searchValues,
        employeeIds: [employeeId],
        permissionKey: FeatureKey.ChatterReportsOverview,
      })).unwrap()
      setChatterSalesReport(result)
      setLoadingChatterSales(false)
    }
    void loadChatterSalesReport()
  }, [dispatch, employeeId, searchValues])

  const createDateStrData = useCallback((name: keyof typeof ChatterPerformanceLabel) => {
    return chatterPerformance?.dateStrs.map((dateStr, i) => ({
      dateStr,
      name: ChatterPerformanceLabel[name],
      count: chatterPerformance[name as keyof ChatterPerformance][i] as number || 0,
    })) || []
  }, [chatterPerformance])

  const messageCountData = useMemo(() => {
    return [
      ...createDateStrData('messageSent'),
      ...createDateStrData('ppvsSent'),
      ...createDateStrData('ppvsUnlocked'),
    ]
  }, [createDateStrData])

  const fansChattedData = useMemo(() => createDateStrData('fansChatted'), [createDateStrData])
  const fansActivityData = useMemo(() => createDateStrData('chatterActivity'), [createDateStrData])
  const goldenRatioData = useMemo(() => createDateStrData('goldenRatio'), [createDateStrData])
  const unlockRatioData = useMemo(() => createDateStrData('unlockRatio'), [createDateStrData])

  return (
    <Page hideHeader contentClassName={s.reportsPerformance}>
      <Flex vertical>
        <Spin spinning={loadingEmployees}>
          {employeeOptions.length > 0 && employeeId !== undefined &&
            <EmployeeSelectForm
              value={employeeId}
              employeeOptions={employeeOptions}
              onChange={id => setEmployeeId(id)}
            />
          }
        </Spin>
        {employeeId !== undefined &&
          <ChatterOverviewSales
            loading={loadingChatterSales}
            salesDetails={chatterSalesReport?.details}
          />
        }
        <Spin spinning={loadingChatterPerformance}>
          <ChatterPerformanceMessageCount data={messageCountData} />
          <Row>
            <Col span={12}>
              <ChatterPerformanceFanChatted data={fansChattedData} />
            </Col>
            <Col span={12}>
              <ChatterPerformanceActivity data={fansActivityData} />
            </Col>
          </Row>
          <Row>
            <Col span={12}>
              <ChatterPerformanceGoldenRatio data={goldenRatioData} />
            </Col>
            <Col span={12}>
              <ChatterPerformanceUnlockRatio data={unlockRatioData} />
            </Col>
          </Row>
        </Spin>
      </Flex>
    </Page>
  )
}
